<template>
  <div class="album-play-container">
    <!-- 顶部导航栏 -->
    <div class="header-bar">
      <div class="left" @click="$router.go(-1)">
        <i class="el-icon-arrow-left" />
        <span class="nav-title">{{ albumInfo.albumName || '连续播放' }}</span>
      </div>
    </div>

    <!-- 视频播放区域 -->
    <div class="player-wrapper">
      <video
        ref="videoPlayer"
        class="native-video"
        controls
        autoplay
        playsinline
        webkit-playsinline
        x5-video-player-type="h5-page"
        x5-video-player-fullscreen="true"
        @ended="handleVideoEnded"
        @error="handleVideoError"
      />
      <div v-if="loadingVideo" class="loading-player">
        <i class="el-icon-loading" />
        <span>加载视频中...</span>
      </div>
    </div>

    <!-- 当前播放视频信息 -->
    <div v-if="currentVideo" class="current-playing-info">
      <h3 class="video-title">{{ currentVideo.title }}</h3>
      <div class="up-info-row">
        <img :src="currentVideo.user.avatarUrl" class="up-avatar" alt="avatar">
        <span class="up-name">{{ currentVideo.user ? currentVideo.user.screenName : '匿名用户' }}</span>
      </div>
    </div>

    <!-- 选集列表区域 -->
    <div class="playlist-section">
      <div class="section-header">
        <span class="title-text">播放列表</span>
        <span class="count-badge">共 {{ videoList.length }} 集</span>
      </div>
      <div class="episodes-list">
        <div
          v-for="(item, index) in videoList"
          :key="item.videoId"
          class="episode-item"
          :class="{ active: currentVideo && currentVideo.videoId === item.videoId }"
          @click="playEpisode(item)"
        >
          <div class="ep-left">
            <span class="index-num">{{ index + 1 }}</span>
            <span class="ep-title">{{ item.title }}</span>
          </div>
          <div class="ep-right">
            <span v-if="currentVideo && currentVideo.videoId === item.videoId" class="playing-status">
              <i class="el-icon-video-play" /> 播放中
            </span>
            <span v-else class="duration-text">{{ item.duration }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAlbumItems } from '@/api/vod'
import { videoUrl } from '@/api/vod'

export default {
  name: 'AlbumPlaylistMobile',
  data() {
    return {
      albumId: null,
      albumInfo: {},
      videoList: [],
      currentVideo: null,
      loadingVideo: false
    }
  },
  created() {
    this.albumId = this.$route.params.albumId
    this.initPlaylist()
  },
  methods: {
    initPlaylist() {
      getAlbumItems({ albumId: this.albumId, pn: 1 }).then(resp => {
        if (resp && resp.code === 0 && resp.data) {
          this.albumInfo = resp.data.albumInfo || {}
          const list = resp.data.videoList?.list || []
          this.videoList = list
          if (list.length > 0) {
            this.playEpisode(list[0]) // 默认播放第一集
          }
        }
      }).catch(err => {
        console.error(err)
      })
    },
    async playEpisode(item) {
      this.currentVideo = item
      this.loadingVideo = true

      try {
        const resp = await videoUrl(item.videoId)
        if (resp && resp.code === 0) {
          const urls = resp.data.urls
          if (urls.length === 0) {
            this.$message.error('没有视频资源 URL')
            return
          }

          const nextUrl = urls[0].url
          const videoEl = this.$refs.videoPlayer

          if (videoEl) {
            videoEl.src = nextUrl
            videoEl.load()
            const playPromise = videoEl.play()
            if (playPromise !== undefined) {
              playPromise.catch(err => {
                console.warn('Auto-play prevented:', err)
              })
            }
          }
        } else {
          this.$message.error('获取视频播放地址失败')
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingVideo = false
      }
    },
    handleVideoEnded() {
      const currentIndex = this.videoList.findIndex(v => v.videoId === this.currentVideo.videoId)
      if (currentIndex !== -1 && currentIndex < this.videoList.length - 1) {
        const nextItem = this.videoList[currentIndex + 1]
        this.playEpisode(nextItem)
        this.$message.success(`自动为您播放下一集：${nextItem.title}`)
      } else {
        this.exitFullscreen()
        this.$message.info('收藏夹列表已播放完毕')
      }
    },
    exitFullscreen() {
      const videoEl = this.$refs.videoPlayer

      if (videoEl && typeof videoEl.webkitExitPresentationMode === 'function') {
        videoEl.webkitExitPresentationMode()
      } else if (videoEl && typeof videoEl.webkitExitFullscreen === 'function') {
        videoEl.webkitExitFullscreen()
      }

      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    },
    handleVideoError() {
      this.loadingVideo = false
    }
  }
}
</script>

<style scoped lang="scss">
.album-play-container {
  min-height: 100vh;
  background-color: #f4f5f7;
  padding-top: 50px;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.header-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  padding: 0 15px;
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 500;
    color: #303133;
    cursor: pointer;
    width: 100%;

    i { font-size: 18px; }

    .nav-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.player-wrapper {
  position: relative;
  width: 100%;
  height: 220px;
  background-color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;

  .native-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .loading-player {
    position: absolute;
    color: #fff;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.6);
    padding: 12px 24px;
    border-radius: 8px;
    pointer-events: none;

    i { font-size: 24px; }
  }
}

.current-playing-info {
  background-color: #ffffff;
  padding: 14px 16px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  .video-title {
    font-size: 16px;
    font-weight: bold;
    color: #212121;
    margin: 0 0 10px 0;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .up-info-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .up-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
    }

    .up-name {
      font-size: 12px;
      color: #999999;
    }
  }
}

.playlist-section {
  background-color: #ffffff;
  padding: 14px 16px;
  border-radius: 12px 12px 0 0;
  margin: 0 8px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.02);

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .title-text {
      font-size: 15px;
      font-weight: bold;
      color: #212121;
    }

    .count-badge {
      font-size: 12px;
      color: #999999;
    }
  }

  .episodes-list {
    max-height: calc(100vh - 380px);
    overflow-y: auto;

    .episode-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 10px;
      border-bottom: 1px solid #f6f7f9;
      cursor: pointer;
      border-radius: 8px;
      transition: background-color 0.2s;

      &:active {
        background-color: #f1f2f5;
      }

      &.active {
        background-color: #fff0f5;

        .index-num {
          color: #fb7299;
          font-weight: bold;
        }

        .ep-title {
          color: #fb7299;
          font-weight: 500;
        }
      }

      .ep-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        overflow: hidden;
        margin-right: 10px;

        .index-num {
          width: 20px;
          font-size: 13px;
          color: #999999;
          text-align: center;
          flex-shrink: 0;
        }

        .ep-title {
          font-size: 13px;
          color: #212121;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .ep-right {
        flex-shrink: 0;

        .playing-status {
          font-size: 12px;
          color: #fb7299;
          display: flex;
          align-items: center;
          gap: 4px;
          font-weight: 500;
        }

        .duration-text {
          font-size: 12px;
          color: #999999;
        }
      }
    }
  }
}
</style>
